import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { format } from 'date-fns';
import RegressionValidationRunDetails from './RegressionValidationRunDetails';
import RegressionPartialMatchReviewDialog from './RegressionPartialMatchReviewDialog';
import {
  formatGenerationNoOpReason,
  getGenerateMissingTestsState,
} from './regressionCoveragePresentation';
import { getValidationRunListFields } from './regressionCoverageEvidencePresentation';

const TERMINAL_STATUSES = new Set(['completed', 'failed']);

const RUN_TYPE_LABELS = {
  validation: 'Coverage validation',
  generation: 'Test generation',
};

const STATUS_CHIP_COLOR = {
  completed: 'success',
  failed: 'error',
  running: 'info',
  queued: 'warning',
};

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'yyyy-MM-dd HH:mm');
};

const EMPTY_STRUCTURED_REPORT = {
  summary: {},
  module_rollup: [],
  covered_units: [],
  partial_units: [],
  missing_units: [],
  generation_summary: {},
  generated_tests: [],
};

const RegressionRunDetails = ({ run }) => {
  if (!run) return null;
  if (run.error) {
    return <Alert severity="error">{run.error}</Alert>;
  }

  const structured = run.structured_report || EMPTY_STRUCTURED_REPORT;
  const summary = structured.summary || {};
  const generationSummary = structured.generation_summary || {};
  const generatedTests = structured.generated_tests || [];
  const blockedUnits = structured.blocked_units || [];
  const noOpReason = generationSummary.no_op_reason || run.report?.no_op_reason;
  const generationScope = generationSummary.generation_scope || run.report?.generation_scope || {};

  if (run.run_type === 'generation') {
    const noOpMessage = formatGenerationNoOpReason(noOpReason, generationScope);
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Summary</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Validation run: {generationSummary.validation_run_id || summary.validation_run_id || '-'}
        </Typography>
        {noOpMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {noOpMessage}
            {generationScope.partial_units_available > 0 && noOpReason === 'no_missing_units' && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Partial units available: {generationScope.partial_units_available}. Open the latest validation run to review partial matches.
              </Typography>
            )}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          Generated tests are suggestions pending approval. They do not count as covered until approved.
        </Alert>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Missing available</Typography><Typography variant="h6">{generationSummary.missing_units_available ?? generationScope.missing_units_available ?? '-'}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Partial available</Typography><Typography variant="h6">{generationSummary.partial_units_available ?? generationScope.partial_units_available ?? '-'}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Generation cap</Typography><Typography variant="h6">{generationSummary.generated_limit ?? generationScope.generated_limit ?? '-'}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Created</Typography><Typography variant="h6">{generationSummary.created ?? 0}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Duplicates suppressed</Typography><Typography variant="h6">{generationSummary.duplicate_suppressed ?? 0}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Review duplicate flagged</Typography><Typography variant="h6">{generationSummary.review_duplicate_flagged ?? 0}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Blocked</Typography><Typography variant="h6">{generationSummary.blocked ?? 0}</Typography></Paper></Grid>
          <Grid item xs={6} sm={4} md={2}><Paper sx={{ p: 1.5 }}><Typography variant="caption">Pending approval</Typography><Typography variant="h6">{generationSummary.generated_tests_pending_approval ?? generatedTests.length}</Typography></Paper></Grid>
        </Grid>
        {(generationSummary.created ?? 0) === 0
          && ((generationSummary.duplicate_suppressed ?? 0) > 0
            || (generationSummary.blocked ?? 0) > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            0 created because {generationSummary.duplicate_suppressed ?? 0} duplicate(s) were suppressed
            {(generationSummary.blocked ?? 0) > 0
              ? ` and ${generationSummary.blocked} unit(s) were blocked as near-duplicates`
              : ''}
            .
          </Alert>
        )}
        {(generationSummary.review_duplicate_flagged ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {generationSummary.review_duplicate_flagged} suggestion(s) were created with a possible-duplicate review flag. Review related tests before approval — this is not a failure.
          </Alert>
        )}
        {generationSummary.blocked > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {generationSummary.blocked} missing unit(s) were blocked because a related test scored above the near-duplicate threshold.
          </Alert>
        )}
        {blockedUnits.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Blocked units (near-duplicate)
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Coverage unit</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Possible related tests</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockedUnits.map((item) => (
                    <TableRow key={item.coverage_unit_id}>
                      <TableCell>{item.coverage_unit_name || item.coverage_unit_id}</TableCell>
                      <TableCell>{item.reason || item.recommended_action || 'Near-duplicate threshold exceeded'}</TableCell>
                      <TableCell>
                        {(item.possible_related_tests || [])
                          .map((test) => test.test_title || test.test_id)
                          .join('; ') || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Generated test suggestions</Typography>
        {generatedTests.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No generated tests in this run.
            {(generationSummary.duplicate_suppressed ?? 0) > 0 || (generationSummary.blocked ?? 0) > 0
              ? ' See summary above for duplicate suppressions and blocked units.'
              : ''}
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Test</TableCell>
                  <TableCell>Source coverage unit</TableCell>
                  <TableCell>Source requirement</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generatedTests.map((item) => (
                  <TableRow key={item.test_id}>
                    <TableCell>{item.test_title || item.test_id}</TableCell>
                    <TableCell>{item.coverage_unit_name || item.coverage_unit_id || '-'}</TableCell>
                    <TableCell>{item.source_pkb_entry_title || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.status_label || 'Pending approval'}
                        color={item.requires_duplicate_review ? 'warning' : 'warning'}
                        variant={item.requires_duplicate_review ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {generationSummary.post_validation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Follow-up validation queued: {generationSummary.post_validation.validation_run_id}
          </Alert>
        )}
      </Box>
    );
  }

  return <RegressionValidationRunDetails run={run} />;
};

const RegressionSuiteHealth = ({ selectedProject, authenticatedUser, apiClient }) => {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [latestValidationRunId, setLatestValidationRunId] = useState(null);
  const [latestValidationSummary, setLatestValidationSummary] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [workerHealth, setWorkerHealth] = useState(null);
  const [workerHealthError, setWorkerHealthError] = useState(null);
  const [partialReviewOpen, setPartialReviewOpen] = useState(false);
  const [partialReviewRunId, setPartialReviewRunId] = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [runToArchive, setRunToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const projectId = selectedProject?.id || selectedProject?.project_id;
  const isAdmin = ['admin', 'project_admin', 'super_admin'].includes((authenticatedUser?.role || '').toLowerCase());

  const activeRunIds = useMemo(
    () => runs.filter((r) => !TERMINAL_STATUSES.has((r.status || '').toLowerCase())).map((r) => r.run_id),
    [runs]
  );

  const workerHealthChip = useMemo(() => {
    const status = (workerHealth?.status || 'unknown').toLowerCase();
    if (status === 'healthy') return { label: 'Worker: Healthy', color: 'success', variant: 'filled' };
    if (status === 'pending') return { label: 'Worker: Pending', color: 'warning', variant: 'outlined' };
    if (status === 'warning') return { label: 'Worker: Warning', color: 'error', variant: 'filled' };
    return { label: 'Worker: Unknown', color: 'default', variant: 'outlined' };
  }, [workerHealth]);

  const workerHealthTooltip = useMemo(() => {
    if (!workerHealth) return 'Worker health unavailable';
    const queued = workerHealth.queued_count ?? 0;
    const running = workerHealth.running_count ?? 0;
    const oldestQueued = workerHealth.oldest_queued_age_seconds ?? 0;
    const oldestRunning = workerHealth.oldest_running_age_seconds ?? 0;
    const recommendation = workerHealth.recommendation || 'No recommendation';
    return `Queued: ${queued} | Running: ${running} | Oldest queued: ${oldestQueued}s | Oldest running: ${oldestRunning}s | ${recommendation}`;
  }, [workerHealth]);

  const generateState = useMemo(
    () => getGenerateMissingTestsState({
      latestValidationRunId,
      latestValidationSummary,
    }),
    [latestValidationRunId, latestValidationSummary]
  );

  const completeDisabledReason = generateState.enabled ? '' : generateState.reason;

  const loadRuns = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await apiClient.makeRequest(`/regression/runs?project_id=${projectId}&limit=50`, { method: 'GET' });
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to load regression runs');
      }
      const nextRuns = response.runs || [];
      setRuns(nextRuns);
      setLatestValidationRunId(response.latest_completed_validation_run_id || null);
      setLatestValidationSummary(response.latest_completed_validation_summary || null);
    } catch (err) {
      setError(err.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [apiClient, projectId]);

  const loadWorkerHealth = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.makeRequest(
        `/regression/worker/health?project_id=${projectId}&stalled_after_seconds=120`,
        { method: 'GET' }
      );
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to load worker health');
      }
      setWorkerHealth(response.worker_health || null);
      setWorkerHealthError(null);
    } catch (err) {
      setWorkerHealthError(err.message || 'Failed to load worker health');
    }
  }, [apiClient, projectId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    loadWorkerHealth();
  }, [loadWorkerHealth]);

  useEffect(() => {
    if (activeRunIds.length === 0 || !projectId) return undefined;
    const timer = setInterval(() => {
      loadRuns();
    }, 2500);
    return () => clearInterval(timer);
  }, [activeRunIds.length, projectId, loadRuns]);

  useEffect(() => {
    if (!projectId) return undefined;
    const timer = setInterval(() => {
      loadWorkerHealth();
    }, 5000);
    return () => clearInterval(timer);
  }, [projectId, loadWorkerHealth]);

  const handleValidate = async () => {
    if (!projectId || validating) return;
    setValidating(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await apiClient.makeRequest('/regression/validate', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, include_staged: false }),
      });
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to queue validation run');
      }
      setActionMessage(`Validation queued: ${response.run.run_id}`);
      await Promise.all([loadRuns(), loadWorkerHealth()]);
    } catch (err) {
      setError(err.message || 'Failed to queue validation');
    } finally {
      setValidating(false);
    }
  };

  const handleComplete = async () => {
    if (!projectId || !latestValidationRunId || completing || !generateState.enabled) return;
    setCompleting(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await apiClient.makeRequest('/regression/complete', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          validation_run_id: latestValidationRunId,
          module_keys: [],
          rerun_validate: true,
        }),
      });
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to queue generation run');
      }
      setActionMessage(`Generation queued: ${response.run.run_id}`);
      await Promise.all([loadRuns(), loadWorkerHealth()]);
    } catch (err) {
      setError(err.message || 'Failed to queue generation');
    } finally {
      setCompleting(false);
    }
  };

  const reviewPartialDisabledReason = !latestValidationRunId
    ? 'Run a coverage validation first.'
    : ((latestValidationSummary?.partial_units ?? 0) > 0 ? '' : 'No partial matches to review.');

  const openPartialReview = () => {
    if (!latestValidationRunId) return;
    if ((latestValidationSummary?.partial_units ?? 0) <= 0) return;
    setPartialReviewRunId(latestValidationRunId);
    setPartialReviewOpen(true);
  };

  const openRunDetails = async (runId) => {
    if (!runId) return;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSelectedRunDetails(null);
    try {
      const response = await apiClient.makeRequest(`/regression/runs/${runId}/details`, { method: 'GET' });
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to load run details');
      }
      setSelectedRunDetails(response.run);
    } catch (err) {
      setSelectedRunDetails({ error: err.message || 'Failed to load details' });
    } finally {
      setDetailsLoading(false);
    }
  };

  const canArchiveRun = (run) => TERMINAL_STATUSES.has((run?.status || '').toLowerCase());

  const openArchiveDialog = (event, run) => {
    event.stopPropagation();
    if (!canArchiveRun(run)) return;
    setRunToArchive(run);
    setArchiveDialogOpen(true);
  };

  const closeArchiveDialog = () => {
    if (archiving) return;
    setArchiveDialogOpen(false);
    setRunToArchive(null);
  };

  const confirmArchive = async () => {
    if (!runToArchive || archiving) return;
    setArchiving(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await apiClient.makeRequest(`/regression/runs/${runToArchive.run_id}/archive`, {
        method: 'POST',
        body: JSON.stringify({
          run_type: runToArchive.run_type,
          project_id: projectId,
        }),
      });
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to archive run');
      }
      setActionMessage('Run archived.');
      closeArchiveDialog();
      await loadRuns();
    } catch (err) {
      setError(err.message || 'Failed to archive run');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Regression Coverage</Typography>
        <Tooltip title={workerHealthTooltip}>
          <Chip
            size="small"
            label={workerHealthChip.label}
            color={workerHealthChip.color}
            variant={workerHealthChip.variant}
          />
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Validate regression coverage against approved product knowledge, generate missing tests, and track run health.
      </Typography>

      {isAdmin && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button variant="outlined" onClick={handleValidate} disabled={validating}>
            {validating ? 'Queuing...' : 'Validate Coverage'}
          </Button>
          <Tooltip title={completeDisabledReason}>
            <span>
              <Button
                variant="contained"
                onClick={handleComplete}
                disabled={completing || !generateState.enabled}
              >
                {completing ? 'Queuing...' : 'Generate Missing Tests'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={reviewPartialDisabledReason}>
            <span>
              <Button
                variant="outlined"
                onClick={openPartialReview}
                disabled={!latestValidationRunId || (latestValidationSummary?.partial_units ?? 0) <= 0}
              >
                Review Partial Matches
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {actionMessage && <Alert severity="success" sx={{ mb: 2 }}>{actionMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {workerHealthError && <Alert severity="error" sx={{ mb: 2 }}>{workerHealthError}</Alert>}
      {workerHealth?.stalled_queue && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Regression worker looks inactive: queued runs are older than {workerHealth.stalled_after_seconds}s.
          Contact your platform administrator if runs do not start.
        </Alert>
      )}
      {!workerHealth?.stalled_queue && workerHealth?.status === 'pending' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Runs are queued and waiting to be picked up by the regression worker.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Button
          variant="text"
          onClick={() => {
            loadRuns();
            loadWorkerHealth();
          }}
          disabled={loading}
        >
          Refresh Runs
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Run ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Coverage %</TableCell>
              <TableCell>Coverage Units</TableCell>
              <TableCell>Covered</TableCell>
              <TableCell>Partial</TableCell>
              <TableCell>Missing</TableCell>
              <TableCell>Approved Tests Considered</TableCell>
              <TableCell>Generated Tests</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Completed At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => {
              const fields = getValidationRunListFields(run);
              return (
              <TableRow key={run.run_id} hover onClick={() => openRunDetails(run.run_id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{run.run_id}</TableCell>
                <TableCell>{RUN_TYPE_LABELS[run.run_type] || run.run_type}</TableCell>
                <TableCell>
                  <Chip
                    label={run.status}
                    size="small"
                    color={STATUS_CHIP_COLOR[(run.status || '').toLowerCase()] || 'default'}
                  />
                </TableCell>
                <TableCell>{fields.coveragePercent == null ? '-' : `${fields.coveragePercent}%`}</TableCell>
                <TableCell>{fields.coverageUnits == null ? '-' : fields.coverageUnits}</TableCell>
                <TableCell>{fields.covered == null ? '-' : fields.covered}</TableCell>
                <TableCell>{fields.partial == null ? '-' : fields.partial}</TableCell>
                <TableCell>{fields.missing == null ? '-' : fields.missing}</TableCell>
                <TableCell>{fields.approvedTestsDisplay}</TableCell>
                <TableCell>{fields.generatedTests}</TableCell>
                <TableCell>{formatTimestamp(run.created_at)}</TableCell>
                <TableCell>{formatTimestamp(run.completed_at)}</TableCell>
                <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                  {isAdmin && (
                    canArchiveRun(run) ? (
                      <Tooltip title="Archive run">
                        <IconButton
                          size="small"
                          onClick={(event) => openArchiveDialog(event, run)}
                          aria-label="Archive run"
                        >
                          <ArchiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Only completed or failed runs can be archived.">
                        <span>
                          <IconButton
                            size="small"
                            disabled
                            aria-label="Archive run disabled"
                          >
                            <ArchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  )}
                </TableCell>
              </TableRow>
              );
            })}
            {runs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={13}>
                  <Typography variant="body2" color="text.secondary">
                    No regression runs found yet. Admins can queue a coverage validation to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          Run Details
          {selectedRunDetails?.run_type && (
            <Typography variant="body2" color="text.secondary">
              {RUN_TYPE_LABELS[selectedRunDetails.run_type] || selectedRunDetails.run_type}
              {' · '}
              {selectedRunDetails.status}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading && <CircularProgress size={20} />}
          {!detailsLoading && <RegressionRunDetails run={selectedRunDetails} />}
        </DialogContent>
      </Dialog>

      <RegressionPartialMatchReviewDialog
        open={partialReviewOpen}
        onClose={() => setPartialReviewOpen(false)}
        apiClient={apiClient}
        projectId={projectId}
        validationRunId={partialReviewRunId}
      />

      <Dialog open={archiveDialogOpen} onClose={closeArchiveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Archive this run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the run from the Regression Coverage list but keeps the audit record and generated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArchiveDialog} disabled={archiving}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmArchive} disabled={archiving}>
            {archiving ? 'Archiving...' : 'Archive run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegressionSuiteHealth;
